import { useState, useEffect, useRef } from 'react';
import { Search, ChevronRight, ChevronDown, Package, Package2, Zap, Check, SearchX, X, RotateCcw } from 'lucide-react';
import { QuickSaleSheet } from './quick-sale-sheet.jsx';
import { SpontanSheet } from './spontan-sheet.jsx';
import { BundleSheet } from './bundle-sheet.jsx';
import { useAnySheetOpen } from '../../widgets.jsx';
import { EmptyState } from '../../components/boothly/index.js';
import { fmtCurrency } from '../../lib/format.js';
import { Z } from '../../lib/z-index.js';
import { useConfirm } from '../../components/boothly/confirm-provider.jsx';

function formatDuration(startTime) {
  if (!startTime) return '0:00';
  // clamp gegen Future/Null-Start → kein Riesenwert ("11:52" ab Sekunde 1)
  const diff = Math.max(0, Date.now() - new Date(startTime).getTime());
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

// "2:14" → "2 Std. 14 Min." (reine Anzeige, Timer-Logik oben unverändert)
function durationLabel(hm) {
  const [h, m] = String(hm).split(':').map(Number);
  if (!h) return `${m || 0} Min.`;
  return m ? `${h} Std. ${m} Min.` : `${h} Std.`;
}

const getCategory = (item) => item.metadata?.category ?? item.category ?? null;
const COLLAPSE_MS = 280;

export function LiveMarkt({
  session,
  items,
  liveSales = [],
  persistedSoldIds,
  onQuickSale,
  onBundleSale,
  onSpontanSale,
  onVoidSale,
  onEndSession,
  onClose,
  dark,
  getPhotoUrl,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [selectedItem, setSelectedItem] = useState(null);
  const [bundleMode, setBundleMode] = useState(false);
  const [bundleItems, setBundleItems] = useState([]);
  const [bundleSheetOpen, setBundleSheetOpen] = useState(false);
  const [spontanOpen, setSpontanOpen] = useState(false);
  const [soldExpanded, setSoldExpanded] = useState(true);
  const [duration, setDuration] = useState('0:00');
  // Gerade verkaufte Zeilen bleiben kurz sichtbar und kollabieren (rein präsentational —
  // die Verfügbarkeits-Filterung unten ist unverändert).
  const [collapsing, setCollapsing] = useState(() => new Set());
  const collapseTimers = useRef([]);
  const anySheetOpen = useAnySheetOpen();
  const confirm = useConfirm();
  // Undo-Leiste: zeigt den jeweils letzten Verkauf ~6 s im Daumenbereich (Rückgängig = Storno)
  const [undoIndex, setUndoIndex] = useState(null);
  const lastCount = useRef(liveSales.length);
  const undoTimer = useRef(null);

  useEffect(() => () => { collapseTimers.current.forEach(clearTimeout); clearTimeout(undoTimer.current); }, []);

  useEffect(() => {
    const newest = liveSales[liveSales.length - 1];
    // Nur für frisch auf diesem Gerät erfasste Verkäufe (optimistische ls-ID), nicht für rehydrierte
    if (liveSales.length > lastCount.current && String(newest?.id).startsWith('ls-')) {
      setUndoIndex(liveSales.length - 1);
      clearTimeout(undoTimer.current);
      undoTimer.current = setTimeout(() => setUndoIndex(null), 6000);
    } else if (liveSales.length < lastCount.current) {
      setUndoIndex(null);
    }
    lastCount.current = liveSales.length;
  }, [liveSales.length]);
  const undoEntry = undoIndex != null ? liveSales[undoIndex] : null;
  const undoPending = undoEntry && String(undoEntry.id).startsWith('ls-');

  const voidEntry = async (entry, { ask }) => {
    if (!onVoidSale || !entry) return;
    if (ask) {
      const ok = await confirm({
        title: 'Verkauf stornieren?',
        subtitle: `${entry.name} · ${fmtCurrency(entry.price)} — der Betrag wird abgezogen${entry.kind === 'spontan' ? '.' : ', der Artikel kommt zurück ins Sortiment.'}`,
        confirmLabel: 'Stornieren',
        destructive: true,
      });
      if (!ok) return;
    }
    setUndoIndex(null);
    await onVoidSale(entry);
  };

  const safeItems = items ?? [];
  const soldIds = persistedSoldIds ?? new Set();
  // Verkauft = optimistisch (sold) ODER persistenter Status ODER in dieser Session verkauft (rehydriert)
  const availableItems = safeItems.filter(i => !i.sold && i.status !== 'verkauft' && !soldIds.has(i.id));

  const getImage = (item) => {
    const url = (getPhotoUrl && item.image_key) ? getPhotoUrl(item.image_key) : (item.image || null);
    if (!url) return null;
    return item._photoV ? `${url}?v=${item._photoV}` : url; // Cache-Bust nach Re-Upload
  };

  // Live-Stats + "Verkauft"-Liste aus EINER Quelle (liveSales) — überlebt Remounts,
  // Spontanverkäufe erscheinen garantiert in der Übersicht.
  const totalRevenue = liveSales.reduce((sum, s) => sum + (s.price || 0), 0);
  const salesCount = liveSales.length;

  // Gewinn: nur wenn eine Standgebühr hinterlegt ist (null/undefined → Verhalten wie bisher).
  // Rein lesend auf totalRevenue — Umsatz-Zahl bleibt reiner Umsatz (Kassen-Abgleich).
  const boothFee = session?.booth_fee;
  const hasFee = boothFee != null;
  const gewinn = hasFee ? totalRevenue - boothFee : 0;
  const gewinnPositive = gewinn >= 0;

  const categories = ['Alle', ...new Set(safeItems.map(getCategory).filter(Boolean))];

  useEffect(() => {
    const start = session?.starts_at || session?.startTime;
    if (!start) return;
    setDuration(formatDuration(start));
    const interval = setInterval(() => setDuration(formatDuration(start)), 60000);
    return () => clearInterval(interval);
  }, [session?.starts_at, session?.startTime]);

  const matchesFilter = (item) => {
    if (activeCategory !== 'Alle' && getCategory(item) !== activeCategory) return false;
    if (searchQuery) return (item.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    return true;
  };
  const filteredItems = availableItems.filter(matchesFilter);
  const availableIds = new Set(availableItems.map(i => i.id));
  const displayItems = safeItems.filter(i => (availableIds.has(i.id) || collapsing.has(i.id)) && matchesFilter(i));

  const collapse = (ids) => {
    setCollapsing(prev => new Set([...prev, ...ids]));
    collapseTimers.current.push(setTimeout(() => {
      setCollapsing(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
    }, COLLAPSE_MS));
  };

  const handleItemTap = (item) => {
    if (bundleMode) {
      setBundleItems(prev =>
        prev.find(i => i.id === item.id) ? prev.filter(i => i.id !== item.id) : [...prev, item]
      );
    } else {
      setSelectedItem(item);
    }
  };

  const handleQuickConfirm = (item, price) => {
    collapse([item.id]);
    onQuickSale?.(item, price);
    setSelectedItem(null);
  };

  const handleSpontanConfirm = (price, note) => {
    onSpontanSale?.(price, note);
    setSpontanOpen(false);
  };

  const handleBundleConfirm = (ids, price) => {
    const objs = safeItems.filter(i => ids.includes(i.id));
    collapse(ids);
    onBundleSale?.(objs, price);
    setBundleItems([]);
    setBundleMode(false);
    setBundleSheetOpen(false);
  };

  const onBundleButton = () => {
    if (!bundleMode) { setBundleMode(true); return; }
    if (bundleItems.length > 0) setBundleSheetOpen(true);
    else setBundleMode(false);
  };
  const cancelBundle = () => { setBundleItems([]); setBundleMode(false); };

  return (
    <div
      data-testid="live-markt"
      className={`fixed inset-0 flex flex-col bg-[var(--bg-L0)] text-[var(--label-primary)] ${dark ? 'troedel-dark' : ''}`}
      style={{ zIndex: Z.liveOverlay }}
    >
      {/* Kopf: Minimieren · Ort + Laufzeit · Beenden */}
      <header
        className="shrink-0 px-2"
        style={{ paddingTop: 'max(6px, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-1">
          {/* Minimieren: Live-Markt verlassen, Markt läuft weiter (Rückkehr via Banner) */}
          <button
            data-testid="live-minimize"
            onClick={() => onClose?.()}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--label-secondary)] active:bg-[var(--fill-quaternary)]"
            aria-label="Minimieren"
          >
            <ChevronDown className="h-[26px] w-[26px]" strokeWidth={2.2} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[17px] font-semibold leading-tight">
              {session?.location || session?.name || 'Flohmarkt'}
            </h1>
            <p className="flex items-center gap-1.5 text-[13px] leading-tight text-[var(--label-secondary)]">
              <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--accent-green)]" aria-hidden="true" />
              <span>{`Live seit ${durationLabel(duration)}`}</span>
            </p>
          </div>
          <button
            data-testid="live-beenden"
            onClick={() => onEndSession?.()}
            className="h-11 shrink-0 rounded-full px-3 text-[17px] font-semibold text-[var(--accent-troedel)] active:opacity-60"
            style={{ transitionProperty: 'opacity', transitionDuration: '100ms' }}
          >
            Beenden
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" style={{ paddingBottom: `calc(${undoEntry ? 156 : 96}px + env(safe-area-inset-bottom))` }}>
        {/* Kasse: Umsatz groß + Verkäufe (| Gewinn nur mit hinterlegter Standgebühr) */}
        <section className="px-4 pb-3 pt-2">
          <div className="rounded-[20px] bg-[var(--bg-L1)] px-5 py-4">
            <div className="flex justify-between text-[13px] text-[var(--label-secondary)]">
              <span>Umsatz</span>
              <span>Verkäufe</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-4">
              <p
                data-testid="live-stat-umsatz"
                className="num min-w-0 whitespace-nowrap text-[40px] font-bold leading-none tracking-[-0.02em]"
              >
                {fmtCurrency(totalRevenue)}
              </p>
              <p data-testid="live-stat-verkaeufe" className="num shrink-0 text-[28px] font-semibold leading-none">
                {salesCount}
              </p>
            </div>
            {hasFee && (
              <div className="mt-3.5 flex items-center justify-between gap-3 border-t border-[var(--separator)] pt-3">
                <p className="num min-w-0 truncate text-[15px] text-[var(--label-secondary)]">
                  {`Standgebühr ${fmtCurrency(boothFee)}`}
                </p>
                <p className="flex shrink-0 items-baseline gap-1.5 whitespace-nowrap">
                  <span className="text-[15px] text-[var(--label-secondary)]">Gewinn</span>
                  <span
                    data-testid="live-gewinn"
                    data-positive={gewinnPositive}
                    className="num whitespace-nowrap text-[20px] font-semibold"
                    style={{ color: gewinnPositive ? 'var(--accent-green)' : 'var(--label-secondary)', transition: 'color 200ms var(--ease-out-ios)' }}
                  >
                    {fmtCurrency(gewinn)}
                  </span>
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Suche + Kategorien */}
        <section className="sticky top-0 bg-[var(--bg-L0)] pb-1" style={{ zIndex: 1 }}>
          <div className="px-4">
            <label className="flex h-11 items-center gap-2 rounded-[12px] bg-[var(--fill-tertiary)] px-3">
              <Search className="h-[18px] w-[18px] shrink-0 text-[var(--label-secondary)]" />
              <input
                data-testid="live-search"
                type="search"
                enterKeyHint="search"
                placeholder="Im Sortiment suchen"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-[17px] outline-none placeholder:text-[var(--label-secondary)] [&::-webkit-search-cancel-button]:hidden"
              />
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery('')} aria-label="Suche löschen" className="-mr-2 flex h-11 w-9 items-center justify-center">
                  <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[var(--label-tertiary)]">
                    <X className="h-3 w-3 text-[var(--bg-L1)]" strokeWidth={3} />
                  </span>
                </button>
              )}
            </label>
          </div>
          {categories.length > 2 && (
            <div className="scrollable-x flex gap-2 overflow-x-auto px-4">
              {categories.map(cat => {
                const active = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    data-testid={`live-cat-${cat}`}
                    onClick={() => setActiveCategory(cat)}
                    aria-pressed={active}
                    className="flex h-11 shrink-0 items-center"
                  >
                    <span
                      className={`whitespace-nowrap rounded-full px-3.5 py-[7px] text-[15px] font-semibold ${
                        active
                          ? 'bg-[var(--tint-troedel)] text-[var(--accent-troedel)]'
                          : 'bg-[var(--fill-tertiary)] text-[var(--label-primary)]'
                      }`}
                      style={{ transition: 'background-color 150ms var(--ease-out-ios), color 150ms var(--ease-out-ios)' }}
                    >
                      {cat}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Sortiment */}
        <section className="px-4 pt-2">
          {safeItems.length === 0 ? (
            <EmptyState
              icon={Package2}
              title="Noch keine Artikel"
              description="Füge zuerst Artikel zum Sortiment hinzu, um sie hier zu verkaufen."
            />
          ) : displayItems.length === 0 ? (
            <EmptyState
              variant="compact"
              icon={SearchX}
              title="Keine Treffer"
              cta={searchQuery
                ? { label: 'Suche zurücksetzen', onClick: () => setSearchQuery(''), accent: 'troedel' }
                : undefined}
            />
          ) : (
            <>
              <p className="flex h-7 items-end justify-between px-4 pb-1.5 text-[13px] text-[var(--label-secondary)]">
                <span>{bundleMode ? 'Artikel fürs Bundle antippen' : 'Sortiment'}</span>
                <span className="num">{filteredItems.length}</span>
              </p>
              <div className="overflow-hidden rounded-[16px] bg-[var(--bg-L1)]">
                {displayItems.map((item, idx) => {
                  const selected = !!bundleItems.find(i => i.id === item.id);
                  const img = getImage(item);
                  const reserved = item.status === 'reserviert';
                  const cat = getCategory(item);
                  return (
                    <div key={item.id} className="collapse-row" data-collapsing={collapsing.has(item.id) ? 'true' : 'false'}>
                      <div>
                        <button
                          data-testid="live-item-row"
                          onClick={() => handleItemTap(item)}
                          aria-pressed={bundleMode ? selected : undefined}
                          className={`flex min-h-[64px] w-full items-center gap-3 pl-3 pr-3 text-left active:bg-[var(--fill-quaternary)] ${
                            selected ? 'bg-[var(--tint-troedel)]' : ''
                          }`}
                          style={{ transition: 'background-color 120ms var(--ease-out-ios)' }}
                        >
                          <div className="h-11 w-11 shrink-0 overflow-hidden rounded-[10px]">
                            {img ? (
                              <img src={img} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-[var(--tint-troedel)]">
                                <Package2 className="h-5 w-5 text-[var(--accent-troedel)]" />
                              </div>
                            )}
                          </div>
                          <div className={`flex min-h-[64px] min-w-0 flex-1 items-center gap-2 py-2.5 ${idx > 0 ? 'border-t border-[var(--separator)]' : ''}`}>
                            <div className="min-w-0 flex-1">
                              <p className="line-clamp-2 text-[17px] leading-[1.25]">{item.name}</p>
                              {(cat || reserved) && (
                                <div className="mt-0.5 flex items-center gap-1.5">
                                  {cat && <span className="truncate text-[13px] text-[var(--label-secondary)]">{cat}</span>}
                                  {reserved && (
                                    <span className="shrink-0 rounded-[6px] bg-[var(--tint-yellow)] px-1.5 py-px text-[12px] font-semibold text-[var(--label-primary)]">
                                      Reserviert
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className="num shrink-0 text-[17px] font-semibold">{fmtCurrency(item.price)}</span>
                            {bundleMode ? (
                              <span
                                className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 ${
                                  selected
                                    ? 'border-[var(--accent-troedel)] bg-[var(--accent-troedel)]'
                                    : 'border-[var(--label-tertiary)]'
                                }`}
                              >
                                {selected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                              </span>
                            ) : (
                              <ChevronRight className="h-4 w-4 shrink-0 text-[var(--label-tertiary)]" strokeWidth={2.6} />
                            )}
                          </div>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Verkauft (neueste zuerst wäre Datenänderung → Reihenfolge wie liveSales) */}
        {liveSales.length > 0 && (
          <section className="px-4 pt-6">
            <button
              onClick={() => setSoldExpanded(v => !v)}
              aria-expanded={soldExpanded}
              className="flex h-11 w-full items-center justify-between px-4 text-[13px] text-[var(--label-secondary)]"
            >
              <span>{`Verkauft (${liveSales.length})`}</span>
              <ChevronDown
                className="h-4 w-4"
                style={{ transform: soldExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms var(--ease-out-ios)' }}
              />
            </button>
            {soldExpanded && (
              <div className="overflow-hidden rounded-[16px] bg-[var(--bg-L1)]">
                {liveSales.map((entry, idx) => (
                  <button
                    type="button"
                    key={entry.id}
                    data-testid="live-sold-row"
                    onClick={() => voidEntry(entry, { ask: true })}
                    disabled={!onVoidSale}
                    aria-label={`${entry.name}, ${fmtCurrency(entry.price)} — antippen zum Stornieren`}
                    className="flex min-h-[52px] w-full items-center gap-3 pl-3 pr-4 text-left active:bg-[var(--fill-quaternary)]"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--tint-stand)]">
                      {entry.kind === 'spontan'
                        ? <Zap className="h-4 w-4 text-[var(--accent-stand)]" />
                        : entry.kind === 'bundle'
                          ? <Package className="h-4 w-4 text-[var(--accent-stand)]" />
                          : <Check className="h-4 w-4 text-[var(--accent-stand)]" strokeWidth={2.8} />}
                    </div>
                    <div className={`flex min-h-[52px] min-w-0 flex-1 items-center gap-2 ${idx > 0 ? 'border-t border-[var(--separator)]' : ''}`}>
                      <p className="min-w-0 flex-1 truncate text-[15px]">{entry.name}</p>
                      {entry.original != null && entry.original !== entry.price && (
                        <span className="num shrink-0 text-[13px] text-[var(--label-tertiary)] line-through">
                          {fmtCurrency(entry.original)}
                        </span>
                      )}
                      <span className="num shrink-0 text-[15px] font-semibold">{fmtCurrency(entry.price)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Aktionsleiste im Daumenbereich (versteckt unter offenen Sheets) */}
      {!anySheetOpen && (
        <div
          className="fixed inset-x-0 bottom-0 border-t border-[var(--separator)] px-4 pt-3"
          style={{
            zIndex: 1,
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            background: 'color-mix(in srgb, var(--bg-L0) 90%, transparent)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {undoEntry && !bundleMode && (
            <div
              data-testid="live-undo"
              className="mb-2.5 flex h-12 items-center gap-3 rounded-[14px] bg-[var(--bg-L1)] pl-3.5 pr-1.5 shadow-[var(--shadow-sm)] dark:bg-[var(--bg-L2)]"
              style={{ animation: 'fadeIn 0.2s ease' }}
              role="status"
            >
              <Check className="h-[18px] w-[18px] shrink-0 text-[var(--accent-stand)]" strokeWidth={2.8} />
              <p className="min-w-0 flex-1 truncate text-[15px] font-semibold">{undoEntry.name}</p>
              <span className="num shrink-0 text-[15px] text-[var(--label-secondary)]">{fmtCurrency(undoEntry.price)}</span>
              <button
                type="button"
                data-testid="live-undo-button"
                onClick={() => voidEntry(undoEntry, { ask: false })}
                disabled={undoPending}
                className="flex h-11 shrink-0 items-center gap-1.5 rounded-[10px] px-3 text-[15px] font-semibold text-[var(--accent-troedel)] active:bg-[var(--fill-quaternary)] disabled:opacity-40"
              >
                <RotateCcw className="h-4 w-4" strokeWidth={2.4} />
                Rückgängig
              </button>
            </div>
          )}
          <div className="flex gap-3">
          {bundleMode ? (
            <>
              <button
                onClick={cancelBundle}
                className="flex h-14 flex-1 items-center justify-center rounded-[14px] bg-[var(--bg-L1)] text-[17px] font-semibold active:scale-[0.98] active:bg-[var(--fill-quaternary)] dark:bg-[var(--bg-L2)]"
                style={{ transition: 'transform 100ms var(--ease-out-ios), background-color 100ms' }}
              >
                Abbrechen
              </button>
              <button
                onClick={onBundleButton}
                disabled={bundleItems.length === 0}
                className="flex h-14 flex-[1.4] items-center justify-center gap-2 rounded-[14px] bg-[var(--accent-troedel)] text-[17px] font-semibold text-white shadow-[var(--shine-button)] active:scale-[0.98] disabled:opacity-40"
                style={{ transition: 'transform 100ms var(--ease-out-ios), opacity 150ms' }}
              >
                <Package className="h-5 w-5" />
                {bundleItems.length > 0 ? `Bundle (${bundleItems.length})` : 'Bundle wählen'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onBundleButton}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-[14px] bg-[var(--bg-L1)] text-[17px] font-semibold text-[var(--accent-troedel)] shadow-[var(--shadow-sm)] active:scale-[0.98] active:bg-[var(--fill-quaternary)] dark:bg-[var(--bg-L2)]"
                style={{ transition: 'transform 100ms var(--ease-out-ios)' }}
              >
                <Package className="h-5 w-5" />
                Bundle
              </button>
              <button
                onClick={() => setSpontanOpen(true)}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-[14px] bg-[var(--accent-troedel)] text-[17px] font-semibold text-white shadow-[var(--shine-button)] active:scale-[0.98]"
                style={{ transition: 'transform 100ms var(--ease-out-ios)' }}
              >
                <Zap className="h-5 w-5" />
                Spontan
              </button>
            </>
          )}
          </div>
        </div>
      )}

      {/* Sheets */}
      <QuickSaleSheet
        item={selectedItem}
        imageUrl={selectedItem ? getImage(selectedItem) : null}
        open={!!selectedItem}
        onOpenChange={(o) => { if (!o) setSelectedItem(null); }}
        onSale={handleQuickConfirm}
        dark={dark}
      />
      <SpontanSheet
        open={spontanOpen}
        onOpenChange={setSpontanOpen}
        onSale={handleSpontanConfirm}
        dark={dark}
      />
      <BundleSheet
        items={bundleItems}
        open={bundleSheetOpen}
        onOpenChange={(o) => { if (!o) setBundleSheetOpen(false); }}
        onBundleSale={handleBundleConfirm}
        dark={dark}
      />
    </div>
  );
}
